// ============================================================
// WordInput — word entry UI (overlaid HTML + PIXI decoration)
// ============================================================
import * as PIXI from 'pixi.js';
import { GAME_WIDTH, GAME_HEIGHT, Z } from '../constants.js';

// Panel layout in game-space pixels
const PANEL_W = 520;
const PANEL_H = 200;
const PANEL_X = (GAME_WIDTH  - PANEL_W) / 2;   // 380
const PANEL_Y = (GAME_HEIGHT - PANEL_H) / 2;   // 260

// Input box inside panel (game-space)
const INPUT_GX = PANEL_X + 30;           // game-space left edge of text field
const INPUT_GY = PANEL_Y + 88;           // game-space top edge of text field
const INPUT_GW = PANEL_W - 60;           // game-space width
const INPUT_GH = 30;                     // game-space height
const SUGG_GY  = INPUT_GY + INPUT_GH + 6;

export class WordInput {
  /**
   * @param {PIXI.Container}          stage
   * @param {function(string)}        onSubmit
   * @param {function(string):string[]} getSuggestions
   * @param {HTMLCanvasElement}       canvas   - needed to calculate screen position
   */
  constructor(stage, onSubmit, getSuggestions, canvas) {
    this.stage          = stage;
    this.onSubmit       = onSubmit;
    this.getSuggestions = getSuggestions;
    this._canvas        = canvas;
    this.open           = false;

    this._buildPixiOverlay();
    this._buildHTMLInput();
    this.hide();
  }

  // ─── PIXI overlay (dim + panel frame) ─────────────────────
  _buildPixiOverlay() {
    this.overlay = new PIXI.Container();
    this.overlay.zIndex = Z.ui + 10;
    this.stage.addChild(this.overlay);

    // Dim
    const dim = new PIXI.Graphics();
    dim.rect(0, 0, GAME_WIDTH, GAME_HEIGHT).fill({ color: 0x000000, alpha: 0.5 });
    this.overlay.addChild(dim);

    // Panel background
    const panel = new PIXI.Graphics();
    panel.roundRect(PANEL_X, PANEL_Y, PANEL_W, PANEL_H, 20)
      .fill({ color: 0x0D1B2A, alpha: 0.97 });
    panel.roundRect(PANEL_X, PANEL_Y, PANEL_W, PANEL_H, 20)
      .stroke({ color: 0x1DE9B6, width: 3 });
    panel.roundRect(PANEL_X + 4, PANEL_Y + 4, PANEL_W - 8, 4, 4)
      .fill({ color: 0x1DE9B6, alpha: 0.4 });
    this.overlay.addChild(panel);

    // Title
    const title = new PIXI.Text({
      text: '✨ Cast a Word Spell! ✨',
      style: { fontSize: 24, fill: 0xFFD700, fontWeight: 'bold',
               dropShadow: { color: 0x000000, blur: 6, distance: 2 } },
    });
    title.anchor.set(0.5, 0);
    title.x = GAME_WIDTH / 2;
    title.y = PANEL_Y + 18;
    this.overlay.addChild(title);

    // Subtitle
    const sub = new PIXI.Text({
      text: 'Type a word (or "giant trampoline"!) then press Enter',
      style: { fontSize: 14, fill: 0x80CBC4, fontStyle: 'italic' },
    });
    sub.anchor.set(0.5, 0);
    sub.x = GAME_WIDTH / 2;
    sub.y = PANEL_Y + 52;
    this.overlay.addChild(sub);

    // Input area background (visual only — actual typing happens in HTML input)
    const inputBg = new PIXI.Graphics();
    inputBg.roundRect(PANEL_X + 20, PANEL_Y + 80, PANEL_W - 40, 46, 8)
      .fill({ color: 0x1A237E, alpha: 0.8 });
    inputBg.roundRect(PANEL_X + 20, PANEL_Y + 80, PANEL_W - 40, 46, 8)
      .stroke({ color: 0x3D5AFE, width: 2 });
    this.overlay.addChild(inputBg);

    // Escape hint
    const esc = new PIXI.Text({
      text: 'Escape to cancel',
      style: { fontSize: 13, fill: 0x455A64 },
    });
    esc.anchor.set(0.5, 1);
    esc.x = GAME_WIDTH / 2;
    esc.y = PANEL_Y + PANEL_H - 10;
    this.overlay.addChild(esc);
  }

  // ─── HTML <input> for real keyboard input ─────────────────
  _buildHTMLInput() {
    const inp = document.createElement('input');
    inp.type         = 'text';
    inp.id           = 'wordInput';
    inp.placeholder  = 'e.g. trampoline, giant balloon, robot…';
    inp.autocomplete = 'off';
    inp.spellcheck   = false;

    // Base styles — position will be set dynamically in _reposition()
    Object.assign(inp.style, {
      position:    'fixed',   // fixed so (left,top) = viewport coords, no scroll issues
      fontSize:    '20px',
      fontFamily:  'monospace',
      fontWeight:  'bold',
      color:       '#1DE9B6',
      background:  'transparent',
      border:      'none',
      outline:     'none',
      zIndex:      '9999',
      caretColor:  '#FFD700',
      display:     'none',
      boxSizing:   'border-box',
      padding:     '0 4px',
    });
    document.body.appendChild(inp);
    this._input = inp;

    // Suggestions dropdown
    const sug = document.createElement('div');
    sug.id = 'wordSuggestions';
    Object.assign(sug.style, {
      position:  'fixed',
      display:   'none',
      flexWrap:  'wrap',
      gap:       '6px',
      zIndex:    '9999',
    });
    document.body.appendChild(sug);
    this._sugDiv = sug;

    // Events
    inp.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const val = inp.value.trim();
        if (val) { this.onSubmit(val); inp.value = ''; this._updateSuggestions(''); }
        this.hide();
      } else if (e.key === 'Escape') {
        this.hide();
      }
      e.stopPropagation();
    });
    inp.addEventListener('input', () => this._updateSuggestions(inp.value));
  }

  // ─── Dynamic positioning ───────────────────────────────────
  /**
   * Convert a game-space rectangle to viewport (fixed) coordinates,
   * accounting for whatever CSS scale/offset the canvas has.
   */
  _gameToScreen(gx, gy, gw, gh) {
    const canvas = this._canvas;
    if (!canvas) return { left: gx, top: gy, width: gw, height: gh, fontSize: 20 };

    const rect   = canvas.getBoundingClientRect();
    const scaleX = rect.width  / GAME_WIDTH;
    const scaleY = rect.height / GAME_HEIGHT;

    return {
      left:     rect.left + gx * scaleX,
      top:      rect.top  + gy * scaleY,
      width:    gw * scaleX,
      height:   gh * scaleY,
      fontSize: Math.round(20 * Math.min(scaleX, scaleY)),
    };
  }

  _reposition() {
    const inp  = this._gameToScreen(INPUT_GX, INPUT_GY, INPUT_GW, INPUT_GH);
    const sugg = this._gameToScreen(INPUT_GX, SUGG_GY,  INPUT_GW, 30);

    Object.assign(this._input.style, {
      left:     `${inp.left}px`,
      top:      `${inp.top}px`,
      width:    `${inp.width}px`,
      height:   `${inp.height}px`,
      fontSize: `${inp.fontSize}px`,
    });

    Object.assign(this._sugDiv.style, {
      left:  `${sugg.left}px`,
      top:   `${sugg.top}px`,
      width: `${sugg.width}px`,
    });
  }

  // ─── Suggestion list ───────────────────────────────────────
  _updateSuggestions(partial) {
    const suggestions = this.getSuggestions?.(partial) ?? [];
    this._sugDiv.innerHTML = '';
    if (!suggestions.length) { this._sugDiv.style.display = 'none'; return; }
    this._sugDiv.style.display = 'flex';
    for (const s of suggestions) {
      const span = document.createElement('span');
      span.textContent = s;
      Object.assign(span.style, {
        background: '#1A237E', color: '#C5CAE9',
        border: '1px solid #3F51B5', borderRadius: '4px',
        padding: '2px 8px', fontSize: '13px', cursor: 'pointer',
      });
      span.addEventListener('click', () => {
        this._input.value = s;
        this._updateSuggestions(s);
        this._input.focus();
      });
      this._sugDiv.appendChild(span);
    }
  }

  // ─── Public API ────────────────────────────────────────────
  show() {
    this.open = true;
    this.overlay.visible = true;
    this._reposition();          // ← recalculate every time in case window resized
    this._input.style.display = 'block';
    this._input.value = '';
    this._updateSuggestions('');
    setTimeout(() => this._input.focus(), 40);
  }

  hide() {
    this.open = false;
    this.overlay.visible = false;
    this._input.style.display = 'none';
    this._sugDiv.style.display = 'none';
    this._input.blur();
  }

  toggle() { this.open ? this.hide() : this.show(); }

  destroy() {
    this._input.remove();
    this._sugDiv.remove();
    if (this.overlay.parent) this.overlay.parent.removeChild(this.overlay);
    this.overlay.destroy();
  }
}
